import type { PullResponse } from './api'
import { attendanceKey } from './attendance'
import type { AttendanceRecord, Meeting, Member, Reason } from './types'

/**
 * A client's copy of the shared data (the phone's offline copy, the console's
 * in-memory one), as maps keyed by id (attendance by
 * `attendanceKey`). Replaced, never mutated, so React sees every change.
 */
export interface LocalData {
  members: Map<string, Member>
  meetings: Map<string, Meeting>
  attendance: Map<string, AttendanceRecord>
  reasons: Map<string, Reason>
  /** user id → display name, for "marked by". */
  people: Map<string, string>
}

export const emptyData = (): LocalData => ({
  members: new Map(),
  meetings: new Map(),
  attendance: new Map(),
  reasons: new Map(),
  people: new Map(),
})

/** Records with a local write still queued; a pull must not overwrite them. */
export interface PendingKeys {
  meetings: ReadonlySet<string>
  attendance: ReadonlySet<string>
}

export const NOTHING_PENDING: PendingKeys = { meetings: new Set(), attendance: new Set() }

/**
 * Folds a pull into the phone's copy and reports exactly what changed, so the
 * repo persists the same delta instead of rewriting everything.
 *
 * - A record with a local write still queued is skipped: that write is newer
 *   than anything the server knew when it answered, and will be pushed.
 * - A deleted meeting takes its marks with it, queued or not — there is
 *   nothing left to mark.
 */
export interface PullChanges {
  putMembers: PullResponse['members']
  putMeetings: PullResponse['meetings']
  deleteMeetings: string[]
  putAttendance: PullResponse['attendance']
  deleteAttendance: string[]
  putReasons: PullResponse['reasons']
  deleteReasons: string[]
  people: PullResponse['people']
}

export function applyPull(
  data: LocalData,
  pull: PullResponse,
  pending: PendingKeys,
): { data: LocalData; changes: PullChanges } {
  const members = new Map(data.members)
  const meetings = new Map(data.meetings)
  const attendance = new Map(data.attendance)
  const reasons = new Map(data.reasons)

  const changes: PullChanges = {
    putMembers: pull.members,
    putMeetings: [],
    deleteMeetings: [],
    putAttendance: [],
    deleteAttendance: [],
    putReasons: [],
    deleteReasons: [],
    people: pull.people,
  }

  for (const m of pull.members) members.set(m.id, m)

  for (const mt of pull.meetings) {
    if (mt.deleted) {
      meetings.delete(mt.id)
      changes.deleteMeetings.push(mt.id)
      continue
    }
    if (pending.meetings.has(mt.id)) continue
    meetings.set(mt.id, mt)
    changes.putMeetings.push(mt)
  }

  const dead = new Set(changes.deleteMeetings)
  if (dead.size > 0) {
    for (const [key, r] of attendance) {
      if (dead.has(r.meetingId)) {
        attendance.delete(key)
        changes.deleteAttendance.push(key)
      }
    }
  }

  for (const r of pull.attendance) {
    const key = attendanceKey(r.meetingId, r.memberId)
    if (dead.has(r.meetingId) || pending.attendance.has(key)) continue
    attendance.set(key, r)
    changes.putAttendance.push(r)
  }

  for (const r of pull.reasons) {
    if (r.deleted) {
      reasons.delete(r.id)
      changes.deleteReasons.push(r.id)
    } else {
      reasons.set(r.id, r)
      changes.putReasons.push(r)
    }
  }

  const people = pull.people.length
    ? new Map(pull.people.map((p) => [p.id, p.displayName]))
    : data.people

  return { data: { members, meetings, attendance, reasons, people }, changes }
}
